import React, { PropTypes } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  Field,
  reduxForm,
  getFormValues,
  change as changeFieldValue,
} from "redux-form";
import { TextInput, SelectInput } from "../../common/form";
import { FormattedMessage, intlShape } from "react-intl";

import { DataTable } from "../../common/DataTable";

import validationUtils from "../../../utils/ValidationUtils";
import { SALES_TAXES_VALIDATION_RULES } from "./salesTaxesValidationRules";
import { ADMINISTRATOR, FINTECH } from "../../../utils/Roles";
import userHasAccess from "../../../utils/userHasAccess";
import { MessageBox } from "../../common";

class SalesTaxesDataTable extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.state = {
      readOnly: true,
      currentValues: null,
    };

    this.getData = this.getData.bind(this);
    this.lockForm = this.lockForm.bind(this);
    this.saveForm = this.saveForm.bind(this);
    this.resetForm = this.resetForm.bind(this);
    this.unlockForm = this.unlockForm.bind(this);
  }

  getChildContext() {
    return {
      scene: this.props.scene,
    };
  }

  lockForm(callback) {
    this.setState({ readOnly: true }, callback());
  }

  resetForm() {
    const callback = () => {
      this.props.initialize(this.props.initialValues);
      return false;
    };
    this.lockForm(callback);
  }

  saveForm() {
    const callback = () => {
      const formValues = this.props.formValues;
      for (let key in formValues) {
        if (!formValues.hasOwnProperty(key)) continue;
        if (formValues[key] == "") {
          this.props.changeFieldValue("salesTaxes", key, 0);
        }
      }
      this.props.handleSubmit();
      return false;
    };
    this.lockForm(callback);
  }

  unlockForm() {
    this.resetForm();
    this.setState({ readOnly: false });
  }

  getTaxesForPeriod(values) {
    /**
     * This function returns the taxes for each province based on Id
     * Return types:
     * insurance: number(insurance tax value)
     * tpsTvh : number(gst/hst value)
     * tvpTvq : number(qst value)
     * province : string(Province Id)
     */
    const salesTaxes = this.props.items;
    const salesTaxId = values["value"];
    let result;
    salesTaxes.map((salesTax) => {
      for (const [key, value] of Object.entries(salesTax)) {
        if (key == "salesTaxId" && value == salesTaxId) {
          result = {
            insurance: salesTax["insurance"],
            tpsTvh: salesTax["tpsTvh"],
            tvpTvq: salesTax["tvpTvq"],
            province: salesTax["provinceCode"].replace("CA-", ""),
          };
        }
      }
    });
    return result;
  }

  onChangeDropdown(event, initialValues) {
    const taxes = this.getTaxesForPeriod(event.target);
    taxes &&
      Object.keys(initialValues)
        .filter(
          (c) => !c.startsWith("current") && c.startsWith(taxes["province"])
        )
        .forEach((k) => {
          const key = k.replace(taxes["province"], "");
          this.props.changeFieldValue("salesTaxes", k, taxes[key]);
        });
  }

  onfocusDropdown(event) {
    const nodes = document.getElementsByClassName(
      "fixedDataTableRowLayout_rowWrapper"
    );
    Array.from(nodes).forEach((n) => (n.style["z-index"] = 0));
    event.target.closest(".fixedDataTableRowLayout_rowWrapper").style[
      "z-index"
    ] = 1;
  }

  getCols() {
    let cols = [
      {
        name: "",
        display: (province) => (
          <div>
            <span className="province">
              <FormattedMessage id={`common.provinces.${province}`} />
            </span>
          </div>
        ),
        widthPct: 10.99,
      },
    ];
    cols.push({
      name: (
        <div>
          <FormattedMessage id="salesTaxes.applicablePeriod" />
        </div>
      ),
      display: (province) => {
        const fieldName = "current" + province;
        const options = this.props.options[province];
        return (
          <div>
            <Field
              id={fieldName}
              name={fieldName}
              component={SelectInput}
              props={{
                onChange: (event) => {
                  this.onChangeDropdown(event, this.props.initialValues);
                },
                onFocus: (event) => {
                  this.onfocusDropdown(event);
                },
              }}
              options={options}
              disabled={!this.state.readOnly}
            />
          </div>
        );
      },
      widthPct: 14,
    });
    this.props.taxes.map((tax) => {
      cols.push({
        name: (
          <div>
            <span className="province">
              <FormattedMessage id="salesTaxes.unit" />
            </span>
            <FormattedMessage id={tax.name} />
          </div>
        ),
        display: (province) => {
          const fieldName = province + tax.key;
          return (
            <Field
              key={fieldName}
              id={fieldName}
              name={fieldName}
              type="absNumber"
              disabled={this.state.readOnly}
              component={TextInput}
            />
          );
        },
        widthPct: 7,
      });
    });

    return cols;
  }

  getData() {
    return this.props.rows;
  }

  render() {
    const { readOnly } = this.state;
    return (
      <div className="sales-taxes">
        <div className="edit-info">
          {!readOnly && (
            <MessageBox className="alert-info">
              <FormattedMessage id="salesTaxes.editTaxWarning" />
            </MessageBox>
          )}
        </div>
        <DataTable
          ref="dataTable"
          getData={this.getData}
          cols={this.getCols()}
          data={this.props.rows}
          offset={0}
          rowsPerPage={this.props.rows.length}
          rowHeight={60}
          showFilters={false}
          showOverflow={false}
          totalRows={this.props.rows.length}
        />
        {readOnly && userHasAccess([ADMINISTRATOR, FINTECH], true) && (
          <button className="btn btn-primary" onClick={this.unlockForm}>
            <FormattedMessage id="common.edit" />
          </button>
        )}
        {!readOnly && (
          <span className="buttons">
            <button className="btn btn-primary" onClick={this.saveForm}>
              <FormattedMessage id="common.save" />
            </button>
            <button className="btn btn-default" onClick={this.resetForm}>
              <FormattedMessage id="common.cancel" />
            </button>
          </span>
        )}
      </div>
    );
  }
}

SalesTaxesDataTable.propTypes = {
  changeFieldValue: PropTypes.func,
  formValues: PropTypes.object,
  handleSubmit: PropTypes.func,
  initialize: PropTypes.func,
  initialValues: PropTypes.object,
  provinces: PropTypes.array,
  options: PropTypes.object,
  scene: PropTypes.string,
  rows: PropTypes.array,
  validation: PropTypes.object,
  items: PropTypes.array,
  taxes: PropTypes.number,
};

SalesTaxesDataTable.defaultProps = {
  scene: "",
};

SalesTaxesDataTable.contextTypes = {
  intl: intlShape,
};

SalesTaxesDataTable.childContextTypes = {
  scene: PropTypes.string,
};

function mapStateToProps(state) {
  const data = state.taxes || {};
  return {
    items: data.items || [],
    formValues: getFormValues("salesTaxes")(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    changeFieldValue: bindActionCreators(changeFieldValue, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps, null, {
  withRef: true,
})(
  reduxForm({
    form: "salesTaxes",
    validate: (values) =>
      validationUtils.validate(values, SALES_TAXES_VALIDATION_RULES),
  })(SalesTaxesDataTable)
);



// WEBPACK FOOTER //
// ./src/components/dashboard/sales-taxes/SalesTaxesDataTable.js